import {
  createKeyedResourceCache,
  createResourceCache,
} from './resourceCache';
import type {
  HoldingResponse,
  PortfolioResponse,
  PortfolioSummary,
} from './portfoliosApi';
import type {
  DiversificationScore,
  PortfolioAnalytics,
} from './analyticsApi';
import type { UsageSummary } from './usageApi';
import type { Notification } from './notificationsApi';
import type { HistoricalPerformance } from './portfolioHistoryApi';
import type { OpenPortfolioSummary } from './openPortfolioApi';
import type { UserPreferencesSummary } from './onboardingApi';

/** Shared TTL for dashboard navigations — warm within a session, invalidate on writes. */
export const DASHBOARD_CACHE_TTL_MS = 5 * 60 * 1000;

export interface AnalyticsBundle {
  analytics: PortfolioAnalytics;
  diversification: DiversificationScore;
}

export interface NotificationsBundle {
  list: Notification[];
  unreadCount: number;
}

export const portfoliosListCache = createResourceCache<PortfolioResponse[]>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const portfolioByIdCache = createKeyedResourceCache<PortfolioResponse>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const portfolioSummaryCache = createKeyedResourceCache<PortfolioSummary>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const holdingsCache = createKeyedResourceCache<HoldingResponse[]>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const analyticsCache = createKeyedResourceCache<AnalyticsBundle>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const usageCache = createResourceCache<UsageSummary>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const notificationsCache = createResourceCache<NotificationsBundle>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const portfolioHistoryCache =
  createKeyedResourceCache<HistoricalPerformance[]>({
    ttlMs: DASHBOARD_CACHE_TTL_MS,
  });

export const openPortfolioCache = createResourceCache<OpenPortfolioSummary>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

export const preferencesCache = createResourceCache<UserPreferencesSummary>({
  ttlMs: DASHBOARD_CACHE_TTL_MS,
});

/** Drop portfolio-related caches after create / update / delete / holdings changes. */
export function invalidatePortfolioCaches(portfolioId?: number): void {
  portfoliosListCache.invalidate();
  analyticsCache.invalidateAll();
  if (portfolioId != null) {
    portfolioByIdCache.invalidate(String(portfolioId));
    portfolioSummaryCache.invalidate(String(portfolioId));
    holdingsCache.invalidate(String(portfolioId));
    // History keys are `${id}:${days}` — clear all history for simplicity
    portfolioHistoryCache.invalidateAll();
  } else {
    portfolioByIdCache.invalidateAll();
    portfolioSummaryCache.invalidateAll();
    holdingsCache.invalidateAll();
    portfolioHistoryCache.invalidateAll();
  }
}

export function invalidateUsageCache(): void {
  usageCache.invalidate();
}

export function invalidateNotificationsCache(): void {
  notificationsCache.invalidate();
}

export function invalidatePreferencesCache(): void {
  preferencesCache.invalidate();
}

export function invalidateOpenPortfolioCache(): void {
  openPortfolioCache.invalidate();
}

/** Nuclear option — e.g. sign-out. */
export function invalidateAllDashboardCaches(): void {
  portfoliosListCache.invalidate();
  portfolioByIdCache.invalidateAll();
  portfolioSummaryCache.invalidateAll();
  holdingsCache.invalidateAll();
  analyticsCache.invalidateAll();
  usageCache.invalidate();
  notificationsCache.invalidate();
  portfolioHistoryCache.invalidateAll();
  openPortfolioCache.invalidate();
  preferencesCache.invalidate();
}
